<template>
  <div class="nixia-chat-input-container">
    <div class="nixia-chat-input-wrapper" :class="{ disabled: typing }">
      <template v-if="recording">
        <div class="nixia-chat-input-recording" role="status" aria-live="polite">
          <span class="nixia-chat-input-recording-dot"></span>
          <span class="nixia-chat-input-recording-label">Gravando</span>
          <span class="nixia-chat-input-recording-timer">{{ elapsedLabel }}</span>
        </div>
        <button
          type="button"
          class="nixia-chat-input-action"
          title="Cancelar gravação"
          aria-label="Cancelar gravação"
          @click="discardRecording"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
        <button
          type="button"
          class="nixia-chat-input-action active"
          title="Parar e enviar"
          aria-label="Parar e enviar"
          @click="finishRecording"
        >
          <i class="fa-solid fa-check"></i>
        </button>
      </template>

      <template v-else>
        <textarea
          ref="textarea"
          v-model="text"
          class="nixia-chat-input-textarea"
          :placeholder="placeholder"
          :disabled="transcribing"
          rows="1"
          @keydown.enter.exact.prevent="submit"
          @input="autoResize"
        ></textarea>
        <button
          v-if="isRecordingSupported"
          type="button"
          class="nixia-chat-input-action"
          :class="{ recordable: canRecord }"
          :disabled="!canRecord"
          :title="transcribing ? 'Transcrevendo áudio' : 'Gravar áudio'"
          :aria-label="transcribing ? 'Transcrevendo áudio' : 'Gravar áudio'"
          @click="beginRecording"
        >
          <i :class="transcribing ? 'fa-solid fa-circle-notch fa-spin' : 'fa-solid fa-microphone'"></i>
        </button>
        <button
          type="button"
          class="nixia-chat-input-send"
          :class="{ active: canSend }"
          :disabled="!canSend"
          :title="typing ? 'Aguarde a resposta' : 'Enviar'"
          @click="submit"
        >
          <i :class="typing ? 'fa-solid fa-circle-notch fa-spin' : 'fa-solid fa-arrow-up'"></i>
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";

import nixiaAudioRecorder from "../mixins/nixiaAudioRecorder";

const MIN_AUDIO_BYTES = 1024;

export default {
  name: "NixiaChatInput",
  mixins: [nixiaAudioRecorder],
  data() {
    return {
      text: "",
      transcribing: false,
    };
  },
  computed: {
    ...mapState("nixia", ["typing"]),
    canSend() {
      return !this.typing && !this.transcribing && this.text.trim().length > 0;
    },
    canRecord() {
      return !this.typing && !this.transcribing;
    },
    placeholder() {
      if (this.transcribing) return "Transcrevendo áudio...";
      if (this.typing) return "A Nixia está respondendo...";
      return "Pergunte algo para a Nixia...";
    },
  },
  watch: {
    recording(value) {
      if (value) {
        document.addEventListener("keydown", this.onRecordingKeyDown);
        return;
      }
      document.removeEventListener("keydown", this.onRecordingKeyDown);
    },
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onRecordingKeyDown);
  },
  methods: {
    ...mapActions("nixia", ["transcribeAudio"]),
    submit() {
      if (!this.canSend) return;
      const message = this.text.trim();

      this.$emit("send", message);
      this.text = "";
      this.$nextTick(() => {
        const textarea = this.$refs.textarea;
        if (textarea) textarea.style.height = "auto";
        this.focus();
      });
    },
    beginRecording() {
      if (!this.canRecord) return;
      this.startRecording();
    },
    onRecordingKeyDown(event) {
      if (event.key === "Escape") this.discardRecording();
    },
    onRecordingLimit() {
      this.$toasted.show("Limite de 2 minutos atingido.", { type: "info" });
      this.finishRecording();
    },
    async discardRecording() {
      await this.cancelRecording();
      this.$nextTick(this.focus);
    },
    async finishRecording() {
      const blob = await this.stopRecording();
      if (!blob) return;

      if (blob.size < MIN_AUDIO_BYTES) {
        this.$toasted.show("Gravação muito curta.", { type: "info" });
        this.$nextTick(this.focus);
        return;
      }

      this.transcribing = true;

      try {
        const text = await this.transcribeAudio({
          blob,
          fileName: this.recordingFileName(blob.type),
        });

        if (text) this.$emit("send", text);
      } catch {
        return;
      } finally {
        this.transcribing = false;
        this.$nextTick(this.focus);
      }
    },
    autoResize() {
      const textarea = this.$refs.textarea;
      if (!textarea) return;
      textarea.style.height = "auto";
      textarea.style.height = Math.min(textarea.scrollHeight, 220) + "px";
    },
    focus() {
      this.$refs.textarea?.focus();
    },
  },
};
</script>

<style scoped>
.nixia-chat-input-container {
  width: 100%;
}

.nixia-chat-input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  border-radius: 22px;
  padding: 8px 8px 8px 18px;
  min-height: 52px;
}

.nixia-chat-input-wrapper.disabled {
  opacity: 0.7;
}

.nixia-chat-input-textarea {
  flex: 1;
  background: transparent !important;
  border: none !important;
  outline: none;
  resize: none;
  padding: 6px 0;
  margin: 0;
  font-size: 15px;
  color: var(--input-color) !important;
  line-height: 1.4;
  max-height: 160px;
  overflow-y: auto;
  font-family: inherit;
}

.nixia-chat-input-textarea::placeholder {
  color: var(--text-muted);
}

.nixia-chat-input-recording {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: var(--text-secondary);
}

.nixia-chat-input-recording-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--color-danger);
  animation: nixia-chat-input-pulse 1.2s ease-in-out infinite;
}

.nixia-chat-input-recording-timer {
  font-variant-numeric: tabular-nums;
  color: var(--text-primary);
}

@keyframes nixia-chat-input-pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.25;
  }
  100% {
    opacity: 1;
  }
}

.nixia-chat-input-action,
.nixia-chat-input-send {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  background: var(--bg-elevated);
  color: var(--text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  cursor: not-allowed;
  transition: background 0.2s, color 0.2s;
}

.nixia-chat-input-action.recordable {
  color: var(--text-secondary);
  cursor: pointer;
}

.nixia-chat-input-action.recordable:hover {
  background: var(--bg-hover);
  color: var(--color-brand);
}

.nixia-chat-input-action:not(:disabled) {
  cursor: pointer;
}

.nixia-chat-input-action.active {
  background: var(--color-brand);
  color: var(--text-inverse);
}

.nixia-chat-input-send.active {
  background: var(--color-brand);
  color: var(--text-inverse);
  cursor: pointer;
}
</style>
